import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useModuleEntitlements } from '../modules/module-management';
import { getModuleGroupKeyForPath } from '../modules/module-management/config';
import { useTheme } from '../contexts/ThemeContext';

interface ModuleGuardProps {
  children: React.ReactNode;
}

/**
 * If the current route belongs to a module group that is disabled in entitlements,
 * renders a "This module is disabled" message and a link to dashboard. Otherwise renders children.
 */
const ModuleGuard: React.FC<ModuleGuardProps> = ({ children }) => {
  const location = useLocation();
  const { entitlements } = useModuleEntitlements();
  const { colors } = useTheme();
  const groupKey = getModuleGroupKeyForPath(location.pathname);

  if (groupKey == null) {
    return <>{children}</>;
  }
  if (entitlements[groupKey] !== false) {
    return <>{children}</>;
  }

  return (
    <div className={`flex flex-col items-center justify-center min-h-[60vh] p-8 ${colors.background}`}>
      <div className={`max-w-md w-full rounded-2xl border ${colors.border} ${colors.surface} p-8 sm:p-10 text-center shadow-sm`}>
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700/50">
          <svg className="h-7 w-7 text-gray-500 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
          </svg>
        </div>
        <h2 className={`text-xl font-semibold ${colors.text} mb-2`}>Module not available</h2>
        <p className={`text-sm ${colors.textMuted} mb-6`}>
          This section is currently disabled. If you need access, ask your administrator.
        </p>
        <Link
          to="/dashboard"
          className={`inline-flex items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-sm font-medium transition-colors bg-indigo-600 text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900`}
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
          Go to Dashboard
        </Link>
      </div>
    </div>
  );
};

export default ModuleGuard;
